import { useCallback, useEffect, useRef } from "react"
import { batch, useComputed, useSignal } from "@preact/signals-react"
import { fileSubmit, postSubmit } from "@ui-seeker/utils"
import { isCsvLoading, reportFormData, reportFormErrors, reportFormNumSearches, useCsv } from "Pages/ReportsAlerts/Reports/@signals"
import { useToast } from "@hooks"
import { useValidatorSeeker } from "@ui-seeker/app"
import { array, object } from "yup"
import { useReportBackendApiPath } from "Pages/ReportsAlerts/Reports/@hooks/index"

export default function useUploadCsv() {

	const csvData = useSignal(reportFormData.peek()?.customConfigData || [])
	const { backendUrl } = useReportBackendApiPath()
	const csvUrl = useSignal(null)
	const { showToast } = useToast()
	const fileRef = useRef(null)
	const file = useSignal(null)
	const data = useComputed(() => ({
		csvUrl: csvUrl.value,
		file: file.value,
		fileRef: fileRef.current,
		csvData: csvData.value,
	}))

	const onSubmit = useCallback(input => {
		file.value = input
		if (!input || input.type !== "text/csv") {
			showToast.error('The file type must be CSV!')
			batch(() => {
				isCsvLoading.value = false
				file.value = null
			})
			return
		}
		fileSubmit(`${backendUrl}/upload-custom-csv`, [input])
			.then(({ data, url }) => {
				batch(() => {
					csvData.value = data
					isCsvLoading.value = false
					csvUrl.value = url
					reportFormData.value = {
						...reportFormData.value,
						customConfigData: data,
						customConfigUrl: url,
					}
				})
				postSubmit(`${backendUrl}/num-searches`, reportFormData.value)
					.then(({ numSearches }) => reportFormNumSearches.value = numSearches)
				return { data, url }
			})
			.catch(_ => {
				batch(() => {
					file.value = null
					isCsvLoading.value = false
				})
				showToast.error("Error sending the file, please make sure it's not empty and has a valid format.")
			})
	}, [file.value])

	const {
		isValid
	} = useValidatorSeeker({ customConfigData: csvData.value }, object({ ...useCsv.value && { customConfigData: array().required().min(1) } }))

	const onDelete = () => showToast.confirm(`Are you sure you want to delete ${file.value?.name}?`, {
		onSubmit: () => batch(() => {
			csvData.value = []
			file.value = null
			fileRef.current = null
			reportFormData.value = { ...reportFormData.value, customConfigData: [] }
		})
	})

	const onDownload = useCallback(e => {
		window.open(csvUrl.value)
		if (e.ctrlKey) e.preventDefault()
	}, [])

	useEffect(() => {
		reportFormErrors.value = { ...reportFormErrors.value, customConfigData: isValid }
		return () => {
			reportFormErrors.value = { ...reportFormErrors.value, customConfigData: true }
		}
	}, [isValid])

	useEffect(() => {
		return () => {
			reportFormData.value = { ...reportFormData.value, customConfigData: [] }
		}
	}, [])


	return {
		onSubmit,
		onDelete,
		onDownload,
		data: data.value
	}

}